/**
 * What to CALL the machine an agent runs on, everywhere it is shown.
 *
 * `Origin` is the edge's internal word for "not a mounted remote" (`host === null`).
 * It is a RELATIONSHIP, not a name — it means "the machine you are asking", which is
 * information only to the thing being asked. On screen it says nothing, and with two
 * paired machines it says the same nothing about both.
 *
 * The sidebar already resolved this correctly and kept the rule to itself: a local
 * `nameOf` inside its render, plus a local grouping key. So the agent title pill went
 * on printing `@ Origin` while the rail two hundred pixels away printed `aowl` for the
 * same agent — one place fixed, the other not, because the rule lived in the place
 * that happened to need it first. That is this repo's most-repeated defect and the
 * reason this is a module rather than another local helper.
 *
 * THE ORDER IS THE POINT:
 *   device_name  the ACCOUNT's name for the paired machine, stamped on by the
 *                roster fan-out — the only one of these that survives a rename
 *   host         a remote mounted INTO one of those machines, which is a different
 *                question and still worth answering when it is set
 *   localName    what this edge calls itself, for rows that predate the stamp
 *   'Origin'     last, and only when nothing knows better — kept rather than
 *                replaced with "unknown" because it is what the router itself says,
 *                and inventing a friendlier word would hide that nobody answered
 */
export function machineNameOf(
  /*
   * A STRUCTURAL type, not `Pick<AgentRow, …>`. The two row shapes this is called
   * with disagree on optionality — AgentRow.host is required-and-nullable, IdleAgent's
   * is optional — and naming one of them would have made the OTHER caller the one that
   * has to cast. Both spellings mean "the machine has not said", so the parameter says
   * exactly that and nothing else.
   */
  a: { device_name?: string | undefined; host?: string | null | undefined },
  localName?: string | null,
): string {
  return a.device_name ?? a.host ?? localName ?? 'Origin'
}

/**
 * The same resolution for a grouping key that has ALREADY been reduced to a string.
 *
 * The rail groups rows by name and then has to title the group; by then the row is
 * gone and only the key remains, so `Origin` has to be translated a second time. Kept
 * beside its sibling so the two cannot drift — they were one rule split across two
 * scopes, which is how the pill came to disagree with the heading above it.
 */
export const displayMachineName = (key: string, localName?: string | null): string =>
  key === 'Origin' ? (localName ?? key) : key
